import React from 'react';
import ButtonTableAction from '../../../../components/buttons/ButtonTableAction';
import DefaultDataTable from '../../../../components/default/DefaultDataTable';
import { BatchDataForm, BatchFilters } from '../../../../app/models/Batch';
import useBatchService from '../../../../app/services/hooks/useBatchService';
import BatchFilter from './BatchFilter';
import BatchSwitchChange from '../BatchSwitchChange';

type BatchTableMode = 'default' | 'collection' | 'selection' | 'commercial';

interface Props {
  batches: BatchDataForm[];
  totalRows: number;
  loading?: boolean;
  edit?: (id: number) => void;
  show?: (id: number) => void;
  destroy?: (id: number) => void;
  clone?: (id: number) => void;
  filter: BatchFilters;
  setFilter: (filter: BatchFilters) => void;
  callbackSuccess: () => void;
  onSearch?: () => void;
  mode?: BatchTableMode;
}

const BatchDataTable = ({
  batches,
  totalRows,
  loading = false,
  edit,
  show,
  destroy,
  clone,
  filter,
  setFilter,
  callbackSuccess,
  onSearch,
  // Modo de visualización:
  //  - 'default': tabla completa
  //  - 'collection': sin columnas Producto / Calidad
  //  - 'selection': igual que default
  //  - 'commercial': sin columna Proyecto
  mode = 'default'
}: Props) => {
  const { previewBatchBarcode } = useBatchService();

  const isCollection = mode === 'collection';
  const isCommercial = mode === 'commercial';
  const isSelection = mode === 'selection';

  // Función helper para calcular el porcentaje de uso
  const getUsagePercentage = (capacity: number, quantity: number): number => {
    if (!capacity || capacity === 0) return 0;
    return (quantity / capacity) * 100;
  };

  // Función helper para obtener la clase de badge según disponibilidad
  const getAvailabilityBadge = (availableQuantity: number, capacity: number): { text: string; className: string } => {
    if (!capacity || capacity === 0) {
      return { text: 'Sin stock', className: 'badge bg-secondary' };
    }
    
    const percentage = (availableQuantity / capacity) * 100;
    
    if (percentage === 0) {
      return { text: 'Agotado', className: 'badge bg-danger' };
    } else if (percentage < 20) {
      return { text: 'Bajo', className: 'badge bg-warning' };
    } else if (percentage < 50) {
      return { text: 'Medio', className: 'badge bg-info' };
    } else {
      return { text: 'Disponible', className: 'badge bg-success' };
    }
  };

  // Estilos para celdas de una sola línea con texto cortado
  const cellStyle = {
    whiteSpace: 'nowrap' as const,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
    width: '100%'
  };

  const columns: any[] = [
    { 
      name: 'Id', 
      selector: (row: any) => row.id, 
      sortable: true, 
      sortField: 'id',
      width: '80px',
      cell: (row: BatchDataForm) => (
        <span style={cellStyle}>{row.id}</span>
      )
    },

    // Tipo de Lote: oculto en Lotes Recolección, Selección y Comerciales
    ...(isCommercial || isSelection || isCollection ? [] : [{
      name: 'Tipo de Lote',
      selector: (row: any) => row.batch_category_label ?? row.batch_category,
      sortable: true,
      sortField: 'batch_category',
      width: '120px',
      cell: (row: BatchDataForm) => (
        <span style={cellStyle} title={row.batch_category_label ?? row.batch_category}>
          {row.batch_category_label ?? row.batch_category}
        </span>
      )
    }]),

    { 
      name: 'Centro', 
      selector: (row: any) => row.center, 
      sortable: true, 
      sortField: 'center__name',
      width: '150px',
      cell: (row: BatchDataForm) => (
        <span style={cellStyle} title={row.center}>
          {row.center}
        </span>
      )
    },

    { 
      name: 'Almacén', 
      selector: (row: any) => row.warehouse, 
      sortable: true, 
      sortField: 'warehouse__name',
      width: '180px',
      cell: (row: BatchDataForm) => (
        <span style={cellStyle} title={(row as any).warehouse}>
          {(row as any).warehouse}
        </span>
      )
    },
  ];

  if (!isCommercial) {
    columns.push({
      name: 'Proyecto',
      selector: (row: any) => (row as any).project ?? '',
      sortable: true,
      sortField: 'project',
      width: '150px',
      cell: (row: BatchDataForm) => (
        <span style={cellStyle} title={(row as any).project ?? ''}>
          {(row as any).project ?? '—'}
        </span>
      )
    });

  }

  if (!isCollection) {
    columns.push(
      {
        name: 'Producto',
        selector: (row: any) => (row as any).product,
        sortable: true,
        sortField: 'product__name',
        width: '300px',
        cell: (row: BatchDataForm) => (
          <span style={cellStyle} title={(row as any).product ?? ''}>
            {(row as any).product ?? ''}
          </span>
        )
      },
      {
        name: 'Calidad',
        selector: (row: any) => (row as any).quality,
        sortable: true,
        sortField: 'quality',
        width: '100px',
        cell: (row: BatchDataForm) => (
          <span style={cellStyle} title={(row as any).quality ?? ''}>
            {(row as any).quality ?? ''}
          </span>
        )
      }
    );
  }

  // "En Uso" solo para vistas distintas de recolección
  if (!isCollection) {
    columns.push({
      name: 'En Uso',
      selector: (row: any) => row.quantity,
      sortable: true,
      sortField: 'quantity',
      width: '120px',
      cell: (row: BatchDataForm) => {
        const quantity = parseFloat(row.quantity?.toString() || '0');
        const capacity = parseFloat((row as any).capacity?.toString() || '0');
        const percentage = getUsagePercentage(capacity, quantity);

        return (
          <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            <div className="d-flex align-items-center" style={{ whiteSpace: 'nowrap' }}>
              <span className="me-1">{quantity.toFixed(2)}</span>
              <small className="text-muted">({percentage.toFixed(0)}%)</small>
            </div>
            {quantity > 0 && (
              <div className="progress" style={{ height: '4px', marginTop: '2px', minWidth: '100%' }}>
                <div
                  className="progress-bar bg-primary"
                  style={{ width: `${Math.min(percentage, 100)}%` }}
                  title={`${percentage.toFixed(1)}% en uso`}
                />
              </div>
            )}
          </div>
        );
      }
    });
  }

  // Capacidad: oculta en Lotes Comerciales
  if (!isCommercial) {
    columns.push({
      name: 'Capacidad',
      selector: (row: any) => (row as any).capacity,
      sortable: true,
      sortField: 'capacity',
      width: '120px',
      cell: (row: BatchDataForm) => (
        <span className="fw-semibold" style={cellStyle}>
          {parseFloat((row as any).capacity?.toString() || '0').toFixed(2)}
        </span>
      )
    });
  }

  columns.push({
    name: 'Unidad',
    selector: (row: any) => row.unit,
    sortable: true,
    sortField: 'unit__name',
    width: '100px',
    cell: (row: BatchDataForm) => (
      <span style={cellStyle} title={row.unit}>
        {row.unit}
      </span>
    )
  });

  // Estado: entre Unidad y Activo (Lotes Recolección, Lotes Selección y Lotes Comerciales)
  if (isCollection || isCommercial || isSelection) {
    columns.push({
      name: 'Estado',
      selector: (row: any) => (row as any).availability_status_label ?? (row as any).availability_status ?? '',
      sortable: true,
      sortField: 'availability_status',
      width: '160px',
      cell: (row: BatchDataForm) => {
        const label =
          (row as any).availability_status_label ??
          (row as any).availability_status ??
          '';
        return (
          <span style={cellStyle} title={label}>
            {label || '—'}
          </span>
        );
      }
    });
  }

  columns.push(
    {
      name: 'Activo',
      selector: (row: any) => row.is_active,
      sortable: true,
      sortField: 'is_active',
      width: '90px',
      cell: (row: BatchDataForm) => (
        <BatchSwitchChange
          batchId={row.id!}
          checkValue={row.is_active ?? true}
          callbackSuccess={callbackSuccess}
        />
      )
    },
    {
      name: 'Acciones',
      selector: (row: any) => row.id,
      sortable: false,
      cell: (row: BatchDataForm) => (
        <div>
          <ButtonTableAction
            callbackFunction={() => previewBatchBarcode(row.id!,'png')}
            classIcon="mdi mdi-barcode"
            colorIcon="text-success"
            title="Código de barras"
          />
          {show && (
            <ButtonTableAction
              callbackFunction={() => show(row.id ?? -1)}
              classIcon="mdi mdi-eye"
              colorIcon="text-info"
              title="Ver"
            />
          )}
          {clone && (
            <ButtonTableAction
              callbackFunction={() => clone(row.id ?? -1)}
              classIcon="mdi mdi-content-copy"
              colorIcon="text-primary"
              title="Clonar lote"
            />
          )}
          {edit && (
            <ButtonTableAction
              callbackFunction={() => edit(row.id ?? -1)}
              classIcon="mdi mdi-pencil"
              colorIcon="text-warning"
              title="Editar"
            />
          )}
          {destroy && (
            <ButtonTableAction
              callbackFunction={() => destroy(row.id ?? -1)}
              classIcon="mdi mdi-delete"
              colorIcon="text-danger"
              title="Eliminar"
            />
          )}
        </div>
      )
    }
  );

  return (
    <>
      <div className="row mt-3 mb-3">
        <div className="col">
          {onSearch && (
            <BatchFilter
              filter={filter}
              setFilter={setFilter}
              onSearch={onSearch}
            />
          )}
        </div>
      </div>
      <DefaultDataTable
        columns={columns}
        data={batches}
        progressPending={loading}
        paginationTotalRows={totalRows}
        filter={filter}
        setFilter={setFilter}
      />
    </>
  );
};

export default BatchDataTable;